import ThronVideo from '@components/utils/ThronVideo';
import useMediaQueries from '@stone/hooks/useMediaQueries';
import Icon from '@stone/ui/icon/Icon';
import Image from '@stone/ui/image/Image';
import classNames from 'classnames';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SwiperCore, { Navigation, A11y, Keyboard } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import '@stone/scss/components/focus-on/_focus-on.default.scss';
SwiperCore.use([Keyboard, Navigation, A11y]);
const FocusOn = (props) => {
    const { t } = useTranslation();
    const [activeData, setActiveData] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const isReduced = useReducedMotion();
    const firstRender = useRef(true);
    const swiperRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(!isReduced);
    const slidesArray = useMemo(() => JSON.parse(props.slides), [props.slides]);
    const { isMobile, isTouch, isWideDesktop, isDesktop } = useMediaQueries();
    const isCarousel = useMemo(() => slidesArray.length > 1 && (isMobile || (isTouch && !isWideDesktop)), [isMobile, isTouch, slidesArray]);
    const isMediaPresent = useMemo(() => activeData && (activeData.image || activeData.imageMobile || activeData.thronID || activeData.thronIDMobile), [activeData]);
    const isActive = useCallback((slide) => (activeData === null || activeData === void 0 ? void 0 : activeData.ID) === slide.ID, [activeData]);
    const isFocusable = useCallback((i) => (!isDesktop ? i === activeIndex : i < activeIndex + 3), [activeIndex, isDesktop]);
    const onSlideChange = useCallback((index) => {
        const currentSlide = slidesArray[index];
        if (slidesArray.length && activeData !== currentSlide) {
            setActiveData(currentSlide);
            setActiveIndex(index);
            setIsPlaying(!isReduced);
        }
    }, [slidesArray, activeData]);
    useEffect(() => {
        if (firstRender.current && slidesArray.length && !isCarousel) {
            setActiveData(slidesArray[0]);
            firstRender.current = false;
        }
    }, [isCarousel]);
    const swiperProps = {
        // watchOverflow: true,
        slidesPerView: 'auto',
        watchSlidesProgress: true,
        spaceBetween: 12,
        navigation: {
            prevEl: '.focus-navigation--prev',
            nextEl: '.focus-navigation--next',
        },
    };
    const renderMedia = () => {
        var _a, _b;
        return isMediaPresent && (React.createElement(React.Fragment, null,
            React.createElement(AnimatePresence, null,
                React.createElement(motion.div, { className: "focus-on__media", key: activeData.ID, initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: {
                        duration: 1,
                    } }, activeData.thronID || activeData.thronIDMobile ? (React.createElement("div", { className: "video-container" },
                    React.createElement(ThronVideo, { autoplay: !isReduced, thronID: activeData.thronID, thronMobileID: activeData.thronIDMobile, lockBitrate: activeData === null || activeData === void 0 ? void 0 : activeData.maxBitrate }),
                    React.createElement("div", { className: "btn--container" },
                        React.createElement("button", { id: "focus-on-video-button", className: "visually-hidden", "data-pause": true, "aria-pressed": "false", onClick: () => setIsPlaying(!isPlaying), "aria-hidden": "true", tabIndex: -1 })))) : ((activeData.image || activeData.imageMobile) && React.createElement(Image, { src: (_a = activeData === null || activeData === void 0 ? void 0 : activeData.image) === null || _a === void 0 ? void 0 : _a.path, srcMobile: (_b = activeData === null || activeData === void 0 ? void 0 : activeData.imageMobile) === null || _b === void 0 ? void 0 : _b.path }))))));
    };
    const renderNavigation = () => (React.createElement("div", { className: "navigation container" },
        React.createElement("button", { className: "button focus-navigation--prev", "aria-label": t('carousel.prevslidemessage') },
            React.createElement(Icon, { name: "left-chevron", family: "10" })),
        React.createElement("button", { className: "button focus-navigation--next", "aria-label": t('carousel.nextslidemessage') },
            React.createElement(Icon, { name: "right-chevron", family: "10" }))));
    const renderSlide = (slide, i, isFocusable) => (React.createElement("div", { className: classNames('focus-on__slide', { active: isActive(slide) }), onMouseEnter: !isCarousel ? () => onSlideChange(i) : () => { }, "aria-hidden": !isFocusable },
        React.createElement("div", null,
            React.createElement("p", { className: "focus-on__slide-index font__cta" },
                !isCarousel && React.createElement(Icon, { name: "open", family: "10", autoSize: true }),
                " ",
                (i + 1).toString().padStart(3, '0')),
            React.createElement("div", { className: "focus-on__slide-title" },
                React.createElement("h2", { className: isActive(slide) ? 'font__title' : 'font__cta' }, slide.title))),
        React.createElement("a", { href: slide === null || slide === void 0 ? void 0 : slide.link_url, className: "button button--tertiary inverted", onFocus: () => onSlideChange(i), tabIndex: isFocusable ? 0 : -1 },
            React.createElement("span", null, slide === null || slide === void 0 ? void 0 : slide.link_label)),
        (slide.thronID || slide.thronIDMobile) && (React.createElement("div", { className: "focus-on__slide-playbutton" },
            React.createElement("button", { className: classNames('btn--pause button button--quaternary inverted', { 'not-carousel': !isCarousel }), "aria-label": t('button.pause'), onFocus: () => onSlideChange(i), onClick: () => {
                    const button = document.querySelector('button#focus-on-video-button');
                    button.click();
                }, tabIndex: isFocusable ? 0 : -1 },
                React.createElement(Icon, { family: "16", name: isPlaying ? 'pause' : 'play', inverted: true, autoSize: true }),
                React.createElement("span", null, isPlaying ? t('button.pause') : t('button.play')))))));
    const renderCarousel = () => {
        return (React.createElement(React.Fragment, null,
            React.createElement(Swiper, Object.assign({ onInit: (swiper) => {
                    swiperRef.current = swiper;
                    onSlideChange(swiper.activeIndex);
                }, onSlideChange: (swiper) => {
                    swiperRef.current.el.style.setProperty('--swiper-translation', (swiper.translate * -1).toString() + 'px');
                    onSlideChange(swiper.activeIndex);
                }, "aria-roledescription": "carousel" }, swiperProps), slidesArray.length && slidesArray.map((slide, i) => React.createElement(SwiperSlide, { key: `carousel-slide-${i}` }, ({ isVisible }) => renderSlide(slide, i, isVisible))))));
    };
    const renderGrid = () => (React.createElement("div", { className: "row focus-on__grid" }, slidesArray.length &&
        slidesArray.map((slide, i) => (React.createElement("div", { className: "col-lg-6 col-xxl-4", key: `column-slide-${i}` }, renderSlide(slide, i, true))))));
    return (React.createElement(React.Fragment, null,
        renderMedia(),
        React.createElement("div", { className: "focus-on__content--wrapper" },
            React.createElement("div", { className: "focus-on__content container" },
                props.title && React.createElement("h2", { className: "font__title focus-on__title" }, props.title),
                isCarousel ? renderCarousel() : renderGrid(),
                isCarousel && renderNavigation()))));
};
export default FocusOn;
